import React, { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { Input, Button, IconButton } from "components";
import { EMAIL_REGEX } from "utils/constants";
import { truncateText } from "utils/helpers";
import { Member } from "../../types/types";
import { useTranslation } from "react-i18next";
import { useCurrentUser } from "utils/hooks";
import useOrganizationSettings from "utils/hooks/organizations/useOrganizationSettings";
import { ORG_FEATURE_ACCESS_IDS } from "utils/config";
import "./InviteNewInput.styles.scss";

interface InviteNewInputProps {
  addedMembers: Member[];
  onAddMemberEmail: (member: Member) => void;
  onRemoveMemberEmail: (member: Member) => void;
  requiredEmailError: string;
  onUpdateRequiredEmailError: () => void;
}

const InviteNewInput = ({
  addedMembers,
  onAddMemberEmail,
  onRemoveMemberEmail,
  requiredEmailError,
  onUpdateRequiredEmailError,
}: InviteNewInputProps) => {
  const { t } = useTranslation(["common"]);
  const { user } = useCurrentUser();
  const { settings: orgSettings } = useOrganizationSettings();
  const showSendInviteCheckbox = ORG_FEATURE_ACCESS_IDS.includes(
    user?.activeOrganizationId || ""
  );

  // Get autoSendInvites setting from orgSettings
  const autoSendInvitesSetting = orgSettings?.settings?.find(
    (s) => s.settingKey === "autoSendInvites"
  );
  const autoSendInvites = autoSendInvitesSetting
    ? autoSendInvitesSetting.settingValue === true ||
      autoSendInvitesSetting.settingValue === "true"
    : true; // default true

  // Set initial sendInvite state from autoSendInvites
  const [sendInvite, setSendInvite] = useState(autoSendInvites);

  // When orgSettings changes, update sendInvite default
  useEffect(() => {
    setSendInvite(autoSendInvites);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSendInvites]);

  const { handleSubmit, register, errors, setValue, setError, control } =
    useForm();

  const inputValue: string | undefined = useWatch({
    control,
    name: "email",
  });

  useEffect(() => {
    if (inputValue) {
      onUpdateRequiredEmailError();
    }
  }, [inputValue, onUpdateRequiredEmailError]);

  const onSubmit = (data: { email: string }) => {
    if (addedMembers.some((member: Member) => member.email === data.email)) {
      setError("email", {
        type: "custom",
        message: t("groups.add_member_panel.already_added_email_error"),
      });
      return;
    }
    onAddMemberEmail({ email: data.email, error: "", sendInvite });
    setValue("email", "");
    setSendInvite(true); // reset checkbox after add
  };

  return (
    <div className="invite-new-input">
      <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
        <Input
          name="email"
          type="email"
          label={t("groups.add_member_panel.invite_new_label")}
          placeholder={t("groups.add_member_panel.invite_new_placeholder")}
          forwardRef={register({
            required: t("form_errors.email_required"),
            pattern: {
              value: EMAIL_REGEX,
              message: t("form_errors.valid_email"),
            },
          })}
          error={errors?.email?.message || requiredEmailError}
          extraClass="invite-new-input__input"
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            e.key === "Enter" && e.preventDefault();
          }}
        />
        {/* Send invitation checkbox */}
        {showSendInviteCheckbox && (
          <div style={{ margin: "10px 0 20px 0" }}>
            <label>
              <input
                type="checkbox"
                checked={sendInvite}
                onChange={(e) => setSendInvite(e.target.checked)}
              />{" "}
              {t(
                "patients.send_invitation_email_label",
                "Send invitation email"
              )}
            </label>
          </div>
        )}
        {inputValue && (
          <Button
            type="submit"
            name="add_member_button"
            value={t("groups.add_member_panel.add_label")}
            extraClass="invite-new-input__add-btn thin pill"
          />
        )}
        {addedMembers?.length > 0 && (
          <div className="invite-new-input__emails">
            {addedMembers.map((member: Member) => (
              <div key={member.email} className="invite-new-input__email">
                <div>
                  {truncateText(member.email, 50)}
                  {member.error && (
                    <div className="invite-new-input__error">
                      <small>{member.error}</small>
                    </div>
                  )}
                </div>
                <IconButton
                  icon="CloseCrossBold"
                  onClick={() => onRemoveMemberEmail(member)}
                  extraClass="invite-new-input__remove-btn"
                />
              </div>
            ))}
          </div>
        )}
      </form>
    </div>
  );
};

export default InviteNewInput;
